import React, { useEffect, useRef, useState } from "react";
import wordData from "../../data/wordData.json";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeftOutlined, ReadOutlined } from "@ant-design/icons";
import { Modal, Tour } from "antd";
import WordCard from "./WordCard";
import useStore from "../../store/useStore";

const FlashcardGame = ({ selectedWordIds = [], customWords = [], gameId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setRef } = useStore();

  const [words, setWords] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(
    location.state?.currentIndex || 0
  );

  const [showGameResult, setShowGameResult] = useState(false);
  const [isWordListModalOpen, setIsWordListModalOpen] = useState(false);
  const [isExitConfirmModalOpen, setIsExitConfirmModalOpen] = useState(false);

  useEffect(() => {
    const filteredWords =
      selectedWordIds.length > 0
        ? selectedWordIds.map((id) => wordData.find((word) => word.id === id))
        : customWords.map((word) => ({
            ...word,
            learned: false,
          }));

    setWords(filteredWords);
  }, []);

  const [showTutorial, setShowTutorial] = useState(false);

  const ref1 = useRef();
  const ref2 = useRef();
  const ref3 = useRef();
  const ref4 = useRef();
  const ref5 = useRef();

  useEffect(() => {
    setRef("wordBookTutorial", "ref1", ref1.current);
    setRef("wordBookTutorial", "ref2", ref2.current);
    setRef("wordBookTutorial", "ref3", ref3.current);
    setRef("wordBookTutorial", "ref4", ref4.current);
    setRef("wordBookTutorial", "ref5", ref5.current);
  }, [setRef]);

  const tutorialSteps = [
    {
      title: (
        <>
          단어장이 이렇게 멋지게 만들어졌어요 💐
          <br />
          단어장은 단순한 몇가지 기능으로 이루어져있어요.
        </>
      ),
      placement: "bottom",
      target: () => ref1.current,
      nextButtonProps: { children: "다음" },
    },
    {
      title: <>단어장을 클릭하면 정답이 보여요!</>,
      placement: "left",
      target: () => ref2.current,
      prevButtonProps: { children: "이전" },
      nextButtonProps: { children: "다음" },
    },
    {
      title: (
        <>
          스피커 아이콘을 누르면 발음을 들을 수 있고,
          <br />
          옆의 눈 모양을 누르면 정답이 보여요
        </>
      ),
      placement: "right",
      target: () => ref3.current,
      prevButtonProps: { children: "이전" },
      nextButtonProps: { children: "다음" },
    },
    {
      title: (
        <>
          카드 리스트 버튼을 누르면
          <br />
          세팅된 전체 단어 목록을 확인할 수 있어요!
        </>
      ),
      placement: "right",
      target: () => ref4.current,
      prevButtonProps: { children: "이전" },
      nextButtonProps: { children: "다음" },
    },
    {
      title: (
        <>
          도중에 그만하려면 종료하기 버튼을 눌러주세요!
          <br />
          지금까지 진행했던 결과를 정리하는 화면으로
          <br />
          넘어갈거에요~
        </>
      ),
      placement: "top",
      target: () => ref5.current,
      prevButtonProps: { children: "이전" },
      nextButtonProps: { children: "완료" },
    },
  ];

  useEffect(() => {
    const hasSeenWordBookTutorial = localStorage.getItem(
      "hasSeenWordBookTutorial"
    );
    if (!hasSeenWordBookTutorial) {
      setShowTutorial(true);
    }
  }, []);

  const handleClose = () => {
    setShowTutorial(false);
    localStorage.setItem("hasSeenWordBookTutorial", "true");
  };

  const tourStyle = {
    steps: tutorialSteps,
    open: showTutorial,
    onClose: handleClose,
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = (learned) => {
    const updatedWords = [...words];
    updatedWords[currentIndex].learned = learned;

    if (learned) {
      if (currentIndex === words.length - 1) {
        setShowGameResult(true);
        return;
      }
    }

    if (currentIndex < words.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  if (words.length === 0) {
    return (
      <div className="flex justify-center items-center mt-[100px] text-xl text-bold text-gray-400">
        데이터가 없습니다.
      </div>
    );
  }

  const handleHomeClick = () => {
    navigate("/");
  };

  const handleGameEnd = () => {
    setIsExitConfirmModalOpen(true);
  };

  if (showGameResult) {
    navigate("/game-result", {
      state: {
        score: currentIndex + 1,
        totalWords: words.length,
        currentIndex,
        id: gameId,
      },
    });
    return null;
  }

  return (
    <>
      <div className="relative flex flex-col items-center justify-center pt-[50px] md:pt-[80px]">
        <div className="w-full space-y-10" ref={ref1}>
          <div className="flex items-center justify-center ">
            <div
              ref={ref4}
              onClick={() => setIsWordListModalOpen(true)}
              className="flex items-center justify-center gap-2 cursor-pointer text-bold text-lg"
            >
              <div>
                <ReadOutlined />
              </div>
              <p>카드리스트</p>
              <p>
                {currentIndex + 1} / {words.length}
              </p>
            </div>
          </div>

          <div>
            <WordCard
              word={words[currentIndex].content}
              img={words[currentIndex].img}
              meaning={words[currentIndex].kor}
              audio={words[currentIndex].audio}
              onPrev={handlePrev}
              onNext={handleNext}
              canGoPrev={currentIndex > 0}
              refs={{ ref2, ref3 }}
            />
          </div>

          <button
            ref={ref5}
            className="fixed bottom-10 right-20 border py-2 px-10"
            onClick={handleGameEnd}
            disabled={currentIndex === words.length - 1}
          >
            종료하기
          </button>
        </div>
      </div>

      <Modal
        title="전체 단어"
        open={isWordListModalOpen}
        onCancel={() => setIsWordListModalOpen(false)}
        footer={null}
        className="center-modal"
        width={600}
      >
        <div className="space-y-2 my-5 mx-2">
          <div className="max-h-[200px] overflow-y-auto">
            <div className="flex flex-wrap gap-2">
              {words.map((word, index) => (
                <div
                  key={word.id}
                  className="min-w-[75px] py-2 px-4 border rounded-[100px] bg-[#d9d9d9] text-center"
                >
                  <span className="font-bold text-[#66686B]">
                    {word.content}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        title="알림"
        className="center-modal"
        open={isExitConfirmModalOpen}
        onOk={() => {
          setIsExitConfirmModalOpen(false);
          setShowGameResult(true);
        }}
        onCancel={() => setIsExitConfirmModalOpen(false)}
        cancelText="취소"
        okText="확인"
        width={400}
      >
        <p>아직 단어가 남아있습니다.</p>
        <p>그래도 종료할까요?</p>
      </Modal>

      <Tour
        {...tourStyle}
        steps={tutorialSteps}
        indicatorsRender={(current, total) => (
          <span>
            {current + 1} / {total}
          </span>
        )}
        gap={{ radius: 8 }}
      />
    </>
  );
};

export default FlashcardGame;
